<script setup lang="ts">
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import FloatingPopupLayout from '../FloatingPopupLayout.vue';
import DashboardElement from './DashboardElement.vue';
import { type Props, setup } from './floatingMenuPopup.setup';
import MidCategory from './MidCategory.vue';
import MenuElement from './MenuElement.vue';
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';

const props = defineProps<Props>();
const {
  mainMenuRef,
  subMenuRef,
  subMenuPopupStyle,

  subMenuInfo,
  clickedMenu,
  computedDashboardMenu,
  computedAnalysisBoardMenu,
  computedSubMenuList,

  getMidCategoryTitle,

  onHoverSubMenu,
  onClickSubMenu,
  onClickGoToDashboardList,
  clearSubMenu,
  t,
} = setup(props);
</script>

<template>
  <floating-popup-layout
    ref="mainMenuRef"
    class="floating-main-menu"
    :title="props.menuInfo?.text"
  >
    <template #list>
      <dashboard-element
        v-if="props.menuInfo?.dashboardList"
        :dashboard-menu="computedDashboardMenu!"
        :root-menu="props.menuInfo.value"
        menu-type="dashboard"
        :clicked="clickedMenu === BASE_MENU_VALUE.DASHBOARD"
        @hover="onHoverSubMenu"
        @click="onClickSubMenu"
      />
      <dashboard-element
        v-if="
          props.menuInfo?.analysisBoardList &&
          props.analysisBoardMenuList &&
          props.analysisBoardMenuList.length > 0
        "
        :dashboard-menu="computedAnalysisBoardMenu!"
        :root-menu="props.menuInfo.value"
        :clicked="clickedMenu === BASE_MENU_VALUE.ANALYSISBOARD"
        menu-type="analysisBoard"
        @hover="onHoverSubMenu"
        @click="onClickSubMenu"
      />

      <template v-for="(menu, menuIdx) in props.menuInfo?.children">
        <menu-element
          v-if="!menu?.children?.length && menu.text"
          :key="`${menu.value}_${menuIdx}`"
          :menu-info="menu"
          :clicked="clickedMenu === menu.value"
          @hover="onHoverSubMenu"
          @click="onClickSubMenu"
        />
        <mid-category
          v-else-if="menu?.children?.length"
          :key="`category_${menu.value}_${menuIdx}`"
          :title="menu.text"
        >
          <menu-element
            v-for="(info, childMenuIdx) in menu.children"
            :key="`${info.value}_${childMenuIdx}`"
            :menu-info="info"
            :clicked="clickedMenu === info.value"
            @hover="onHoverSubMenu"
            @click="onClickSubMenu"
          />
        </mid-category>
      </template>
    </template>
    <template #addition>
      <floating-popup-layout
        v-if="computedSubMenuList.length > 0"
        ref="subMenuRef"
        class="floating-sub-menu"
        :title="subMenuInfo!.text"
        :style="subMenuPopupStyle"
        @mouseleave="() => clearSubMenu()"
      >
        <template #list>
          <template v-for="(subMenu, subMenuIdx) in computedSubMenuList">
            <menu-element
              v-if="!subMenu.children?.length && subMenu.text"
              :key="`${subMenu.value}_${subMenuIdx}`"
              :menu-info="subMenu"
            />
            <mid-category
              v-else-if="subMenu.children?.length"
              :key="`category_${subMenu.value}_${subMenuIdx}`"
              :title="getMidCategoryTitle(subMenu.text)"
            >
              <menu-element
                v-for="(info, childMenuIdx) in subMenu.children"
                :key="`${info.value}_${childMenuIdx}`"
                :menu-info="info"
              />
            </mid-category>
          </template>
        </template>
      </floating-popup-layout>
      <floating-popup-layout
        v-else-if="subMenuInfo?.value === BASE_MENU_VALUE.DASHBOARD"
        ref="subMenuRef"
        class="floating-sub-menu"
        :title="t('WORD.DASHBOARD')"
        :style="subMenuPopupStyle"
        @mouseleave="() => clearSubMenu()"
      >
        <template #list>
          <li class="dashboard-item-empty__state">
            <div class="dashboard-item-empty__message">
              {{ t('MESSAGE.NO_REGISTERED_DASHBOARD') }}
            </div>
            <base-button
              tag="button"
              class="dashboard-item-empty__button"
              @click="onClickGoToDashboardList"
            >
              {{ t('MESSAGE.GO_TO_DASHBOARD_LIST') }}
              <ev-icon icon="ev-icon-arrow-right2" />
            </base-button>
          </li>
        </template>
      </floating-popup-layout>
    </template>
  </floating-popup-layout>
</template>

<style scoped lang="scss">
.floating-main-menu {
  --sub-menu-initial-position: calc(var(--gnb-menu-width) - var(--gnb-sub-menu-width) - 4px);

  left: var(--sub-menu-initial-position);
  transition: left $animate-gnb;

  &.shown {
    left: (var(--gnb-menu-width)) !important;
  }

  .dashboard-item-empty {
    &__button {
      border-color: var(--color-gray-05-05);
      color: var(--main-font-color);
    }

    [data-theme='dark'] & {
      &__button {
        border-color: var(--color-gray-05-05);
        background-color: var(--color-gray-07-00);
      }
    }
  }
}
.floating-sub-menu {
  left: 100%;
  padding: 0;
}
</style>
